import { HttpChatTransport } from "ai";
import { assistantAPI } from "api";
import { uiMessageToApiMessage } from "./messageUtils";

function transformDataStreamToUiMessageStream(stream) {
  let buffer = "";
  let textId = "text-1";
  let textStarted = false;
  let streamStarted = false;
  let stepIndex = 0;
  let statusIndex = 0;

  const closeTextIfOpen = (controller) => {
    if (!textStarted) {
      return;
    }
    controller.enqueue({ type: "text-end", id: textId });
    textStarted = false;
  };

  const ensureStreamStarted = (controller) => {
    if (streamStarted) {
      return;
    }
    controller.enqueue({ type: "start" });
    controller.enqueue({ type: "start-step" });
    streamStarted = true;
    stepIndex = 1;
    textId = `text-${stepIndex}`;
  };

  const handlePart = (controller, type, value) => {
    switch (type) {
      case "f": {
        controller.enqueue({ type: "start", messageId: value.messageId });
        controller.enqueue({ type: "start-step" });
        streamStarted = true;
        stepIndex += 1;
        textId = `text-${stepIndex}`;
        textStarted = false;
        break;
      }
      case "0": {
        ensureStreamStarted(controller);
        if (!textStarted) {
          controller.enqueue({ type: "text-start", id: textId });
          textStarted = true;
        }
        controller.enqueue({ type: "text-delta", id: textId, delta: value });
        break;
      }
      case "b": {
        ensureStreamStarted(controller);
        controller.enqueue({
          type: "tool-input-start",
          toolCallId: value.toolCallId,
          toolName: value.toolName,
        });
        break;
      }
      case "c": {
        ensureStreamStarted(controller);
        controller.enqueue({
          type: "tool-input-delta",
          toolCallId: value.toolCallId,
          inputTextDelta: value.argsTextDelta,
        });
        break;
      }
      case "9": {
        ensureStreamStarted(controller);
        controller.enqueue({
          type: "tool-input-available",
          toolCallId: value.toolCallId,
          toolName: value.toolName,
          input: value.args,
        });
        break;
      }
      case "a": {
        ensureStreamStarted(controller);
        controller.enqueue({
          type: "tool-output-available",
          toolCallId: value.toolCallId,
          output: value.result,
        });
        break;
      }
      case "2": {
        ensureStreamStarted(controller);
        const parts = Array.isArray(value) ? value : [value];
        parts
          .filter((part) => part?.type === "assistant_status")
          .forEach((part) => {
            statusIndex += 1;
            controller.enqueue({
              type: "tool-input-available",
              toolCallId: `assistant-status-${stepIndex}-${statusIndex}`,
              toolName: "__assistant_status__",
              input: {
                stage: part.stage || "working",
                label: part.label || "Working on your request",
                order: part.order ?? statusIndex,
              },
            });
          });
        break;
      }
      case "3": {
        ensureStreamStarted(controller);
        controller.enqueue({ type: "error", errorText: value });
        break;
      }
      case "e": {
        if (!value.isContinued) {
          closeTextIfOpen(controller);
        }
        controller.enqueue({ type: "finish-step" });
        if (value.isContinued) {
          stepIndex += 1;
          textId = `text-${stepIndex}`;
          textStarted = false;
        }
        break;
      }
      case "d": {
        closeTextIfOpen(controller);
        controller.enqueue({ type: "finish", finishReason: value.finishReason });
        break;
      }
      default:
        break;
    }
  };

  return stream.pipeThrough(new TextDecoderStream()).pipeThrough(
    new TransformStream({
      transform(chunk, controller) {
        buffer += chunk;
        let newlineIndex = buffer.indexOf("\n");
        while (newlineIndex !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);
          if (line) {
            const colonIndex = line.indexOf(":");
            if (colonIndex !== -1) {
              const type = line.slice(0, colonIndex);
              const valueStr = line.slice(colonIndex + 1);
              try {
                handlePart(controller, type, JSON.parse(valueStr));
              } catch (error) {
                // Ignore malformed lines.
              }
            }
          }
          newlineIndex = buffer.indexOf("\n");
        }
      },
      flush(controller) {
        const trailingLine = buffer.trim();
        if (trailingLine) {
          const colonIndex = trailingLine.indexOf(":");
          if (colonIndex !== -1) {
            const type = trailingLine.slice(0, colonIndex);
            const valueStr = trailingLine.slice(colonIndex + 1);
            try {
              handlePart(controller, type, JSON.parse(valueStr));
            } catch (error) {
              // Ignore malformed lines.
            }
          }
        }
        closeTextIfOpen(controller);
      },
    })
  );
}

class DataStreamChatTransport extends HttpChatTransport {
  processResponseStream(stream) {
    return transformDataStreamToUiMessageStream(stream);
  }
}

function parseRequestBody(body) {
  if (!body) {
    return null;
  }

  if (typeof body === "object") {
    return body;
  }

  if (typeof body !== "string") {
    return null;
  }

  try {
    let parsed = JSON.parse(body);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function normalizeApiMessages(messages = []) {
  return messages.map((message) => {
    if (message?.parts) {
      return uiMessageToApiMessage(message);
    }

    return {
      role: message.role,
      content: typeof message.content === "string" ? message.content : "",
    };
  });
}

export function buildAssistantChatPayload(messages, sessionId, claimId) {
  return {
    messages: normalizeApiMessages(messages),
    session_id: sessionId,
    claim_id: claimId,
  };
}

export function createAssistantChatTransport({ sessionIdRef, claimIdRef }) {
  return new DataStreamChatTransport({
    api: assistantAPI.chatApiUrl,
    streamProtocol: "data",
    credentials: "include",
    headers: () => assistantAPI.getAuthHeaders(),
    fetch: async (url, init = {}) => {
      const parsedBody = parseRequestBody(init.body);
      const payload = buildAssistantChatPayload(
        parsedBody?.messages ?? [],
        sessionIdRef.current,
        claimIdRef.current
      );

      const headers = new Headers(init.headers || {});
      headers.set("Content-Type", "application/json");

      return globalThis.fetch(url, {
        method: init.method || "POST",
        headers,
        credentials: init.credentials || "include",
        signal: init.signal,
        body: JSON.stringify(payload),
      });
    },
  });
}
